import { useEffect, useMemo, useState } from "react";
import { supabase } from "./lib/supabase";
import "./vehicle-document-generator.css";

type Version = { id: string; status: string; version_number: number };
type Template = {
  id: string;
  name: string;
  document_type: string;
  source: "vorno" | "stand";
  legal_note: string;
  official_form: boolean;
  selected: boolean;
  versions: Version[];
};
type Center = {
  templates: Template[];
  contexts: Record<string, string | null>;
};

const typeLabels: Record<string, string> = {
  reservation: "Contrato de reserva",
  sale: "Contrato de venda",
  circulation_declaration: "Declaração de circulação",
  registration_request: "Requerimento de registo automóvel",
  consignment_contract: "Contrato de consignação",
  price_sheet: "Preçário com imagem",
  trade_in_receipt: "Recibo de retoma",
  purchase_proposal: "Proposta de compra",
  model_9: "Modelo 9 IMT",
};

function sourceFor(type: string, contexts: Center["contexts"]): [string,string] | null {
  if (type === "reservation") return contexts.reservation ? ["reservation", contexts.reservation] : null;
  if (type === "sale" || type === "registration_request") return contexts.sale ? ["sale", contexts.sale] : type === "registration_request" && contexts.vehicle ? ["vehicle", contexts.vehicle] : null;
  if (type === "consignment_contract") return contexts.consignment ? ["consignment", contexts.consignment] : null;
  if (type === "trade_in_receipt") return contexts.trade_in ? ["trade_in", contexts.trade_in] : null;
  return contexts.vehicle ? ["vehicle", contexts.vehicle] : null;
}

export function VehicleDocumentGenerator({ organizationId, vehicleId, onClose }: { organizationId: string; vehicleId: string; onClose: () => void }) {
  const [center, setCenter] = useState<Center | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  useEffect(() => {
    void supabase.rpc("vehicle_document_center", { p_organization_id: organizationId, p_vehicle_id: vehicleId }).then(({ data, error }) => {
      if (error) setMessage(error.message); else setCenter(data as Center);
    });
  }, [organizationId, vehicleId]);
  const choices = useMemo(() => {
    if (!center) return [];
    const grouped = new Map<string, Template[]>();
    for (const template of center.templates) grouped.set(template.document_type, [...(grouped.get(template.document_type) ?? []), template]);
    return [...grouped.entries()].map(([type, templates]) => {
      const selected = templates.find((item) => item.selected) ?? templates.find((item) => item.source === "stand") ?? templates[0];
      return { type, template: selected, source: sourceFor(type, center.contexts) };
    });
  }, [center]);
  const generate = async (type: string, template: Template, source: string[] | null) => {
    if (!source) return;
    const version = template.versions.find((item) => item.status === "published");
    if (!version) return setMessage("O modelo escolhido ainda não tem uma versão publicada.");
    setBusy(type); setMessage("A gerar, arquivar e preparar o documento para impressão…");
    const { data, error } = await supabase.functions.invoke("generate-process-document", { body: { organization_id: organizationId, version_id: version.id, source_type: source[0], source_id: source[1] } });
    if (error || data?.error) setMessage(data?.error ?? error?.message ?? "Não foi possível gerar o documento.");
    else {
      const documentId = data?.archive_document_id;
      if (documentId) {
        const opened = await supabase.functions.invoke("open-archive-document", { body: { document_id: documentId, purpose: "Impressão de documento gerado da viatura" } });
        if (opened.data?.signed_url) window.open(opened.data.signed_url, "_blank", "noopener,noreferrer");
        else setMessage(opened.data?.error ?? opened.error?.message ?? "O documento foi arquivado, mas não foi possível abrir a impressão.");
      } else setMessage("Documento gerado e arquivado.");
    }
    setBusy(null);
  };
  return <div className="stock-dialog-backdrop vehicle-document-generator-backdrop">
    <section className="vehicle-document-generator" role="dialog" aria-modal="true">
      <header><div><p className="eyebrow">Documentos da viatura</p><h2>Gerar documento</h2><p>São apresentados apenas os documentos compatíveis com os dados já registados.</p></div><button className="stock-close" onClick={onClose}>×</button></header>
      {message && <button type="button" className="stock-notice" onClick={() => setMessage(null)}>{message} ×</button>}
      <div className="vehicle-document-options">
        {!center ? <p>A preparar os documentos…</p> : choices.map(({ type, template, source }) => <article key={type} className={!source ? "unavailable" : ""}>
          <div><strong>{typeLabels[type] ?? template.name}</strong><span>{template.official_form ? "Formulário oficial · apenas preenchimento" : template.source === "stand" ? "Modelo próprio do stand" : "Modelo Stand"}</span><small>{source ? template.legal_note : type === "reservation" ? "Disponível quando existir uma reserva ativa." : type === "sale" ? "Disponível quando existir uma venda associada." : type === "consignment_contract" ? "Disponível para viaturas consignadas." : "Disponível quando existir uma retoma associada."}</small></div>
          <button type="button" disabled={!source || busy !== null} onClick={() => void generate(type, template, source)}>{busy === type ? "A gerar…" : "Gerar"}</button>
        </article>)}
      </div>
    </section>
  </div>;
}
